// Copyright ©️ 2026 eVolve MEP, LLC

import { createContext, type ReactNode, useCallback, useEffect, useMemo, useState } from 'react';

import type { Part, CreateEditPartBody, AssemblyItemId, AssemblyTaskId } from '@evolvemep/api-ts';
import { notifications } from '@mantine/notifications';
import {
  addEdge,
  applyEdgeChanges,
  applyNodeChanges,
  type Edge,
  getOutgoers,
  type Node,
  type NodeProps,
  type OnConnect,
  type OnEdgesChange,
  type OnNodesChange,
  Position,
  type ReactFlowProps,
  ReactFlowProvider,
} from '@xyflow/react';
import { v4 as uuidv4 } from 'uuid';

import type { ShopNavigationRoute } from 'app/ModuleRoutes';
import { useEvolveNavigate } from 'components/Mantine/Navigation/useEvolveNavigate';
import type { StrongOmit, WithRequired } from 'helpers/helper-types';
import { isNil, isNotNil } from 'helpers/isNotNil';
import { useGeneralContext } from 'helpers/useGeneralContext';
import { autoLayoutedNodes } from 'modules/Shop/CatalogSetup/PartEditor/autoLayoutedNodes';
import type {
  NodeType,
  PartNodeType,
  WriteInNodeType,
  TaskNodeType,
  NodeReferenceId,
} from 'modules/Shop/CatalogSetup/PartEditor/nodes/types';
import { type AssemblyEdgeId, type CustomAssembly, CoreNodeId } from 'modules/Shop/CatalogSetup/PartEditor/types';
import { type CreateEditPartFormType, usePartEditor } from 'modules/Shop/CatalogSetup/PartEditor/usePartEditor';
import {
  getNodeInfoFromAssemblyPartItem,
  getNodeInfoFromAssemblyTask,
  parseCustomAssemblyJson,
  stringifyCustomAssemblyJson,
  getNodeInfoFromCorePart,
  getNodeInfoFromAssemblyWriteInItem,
} from 'modules/Shop/CatalogSetup/PartEditor/utils';

export type AssemblyEditorContextType = WithRequired<
  ReactFlowProps,
  'nodes' | 'edges' | 'onNodesChange' | 'onEdgesChange' | 'onConnect'
> & {
  isDirty: boolean;
  addNode: (node: StrongOmit<NodeType, 'id'>) => NodeType;
  saveAssembly: (formValues: CreateEditPartFormType) => Promise<Part>;
  catalogPart: Part | undefined;
  publishing: boolean;
  form: ReturnType<typeof usePartEditor>['form'];
  catalogSetupUrl: ShopNavigationRoute;
  goBackToCatalog: () => void;
};

export const AssemblyEditorContext = createContext<AssemblyEditorContextType | undefined>(undefined);

export const AssemblyEditorProvider = ({ children }: { children: ReactNode }) => {
  const { part, setSelectedTab, publishPart, publishing, form } = usePartEditor();
  const [isDirty, setIsDirty] = useState(false);
  const [nodes, setNodes] = useState<Node[]>([]);
  const [edges, setEdges] = useState<Edge[]>([]);
  const onNodesChange: OnNodesChange = useCallback(
    (changes) => {
      setIsDirty((d) => d || changes.some((c) => c.type !== 'dimensions' && c.type !== 'select'));
      setNodes((nds) => applyNodeChanges(changes, nds));
    },
    [setNodes],
  );
  const onEdgesChange: OnEdgesChange = useCallback(
    (changes) => {
      setIsDirty((d) => d || changes.some((c) => c.type !== 'select'));
      setEdges((eds) => applyEdgeChanges(changes, eds));
    },
    [setEdges],
  );
  const addNode = useCallback<AssemblyEditorContextType['addNode']>((node) => {
    setIsDirty(true);
    const newNode = {
      ...node,
      id: uuidv4(),
    } as NodeType;
    setNodes((nds) => nds.concat(newNode));
    return newNode;
  }, []);
  const onConnect: OnConnect = useCallback(
    (connection) => {
      const hasCycle = (node: Node, visited = new Set<Node['id']>()): boolean => {
        if (visited.has(node.id)) return false;
        visited.add(node.id);

        return getOutgoers(node, nodes, edges).some(
          (outgoer) => outgoer.id === connection.source || hasCycle(outgoer, visited),
        );
      };
      const target = nodes.find((node) => node.id === connection.target);
      if (isNil(target)) return;
      if (hasCycle(target)) {
        notifications.show({
          title: 'Circular reference prevented',
          message: 'Cannot create infinite loops.',
          color: 'red',
        });
        return;
      }
      setIsDirty(true);
      setEdges((eds) => addEdge(connection, eds));
    },
    [edges, nodes],
  );

  // Load assembly into the editor
  useEffect(() => {
    setIsDirty(isNil(part));

    const assembly = isNotNil(part) ? parseCustomAssemblyJson(part.assemblyEditorLayout) : undefined;
    const coreNodePos = assembly?.nodePositions[CoreNodeId];
    const newNodes = [
      {
        // Our base assembly node is not stored in the list of `part.assemblyItems`
        id: CoreNodeId,
        ...getNodeInfoFromCorePart({
          name: part?.name ?? 'New assembly',
          description: part?.description ?? null,
          unitOfMeasureName: part?.unitOfMeasureName ?? 'assembly',
          unitOfMeasureCode: part?.unitOfMeasureCode ?? 'asm',
          image: part?.image,
        }),
        // but we *do* store the node's position in `part.assemblyEditorLayout.nodePositions`
        position: {
          x: coreNodePos?.x ?? 0,
          y: coreNodePos?.y ?? 0,
        },
      },
      ...(part?.assemblyPartItems.map((item) => {
        const { x = 0, y = 0 } = assembly?.nodePositions[item.assemblyItemId] ?? {};
        return {
          id: item.assemblyItemId,
          ...getNodeInfoFromAssemblyPartItem(item),
          position: { x, y },
        };
      }) ?? []),
      ...(part?.assemblyWriteInItems.map((item) => {
        const { x = 0, y = 0 } = assembly?.nodePositions[item.assemblyItemId] ?? {};
        return {
          id: item.assemblyItemId,
          ...getNodeInfoFromAssemblyWriteInItem(item),
          position: { x, y },
        };
      }) ?? []),
      ...(part?.assemblyTasks.map((task) => {
        const { x = 0, y = 0 } = assembly?.nodePositions[task.assemblyTaskId] ?? {};
        return {
          id: task.assemblyTaskId,
          ...getNodeInfoFromAssemblyTask(task),
          position: { x, y },
        };
      }) ?? []),
    ];

    const newEdges =
      part?.assemblyTasks.flatMap((task) =>
        task.predecessors.map((predecessor) => {
          const edge = assembly?.edges.find(
            (e) => e.beginNodeId === predecessor.assemblyTaskId && e.endNodeId === task.assemblyTaskId,
          );
          return {
            id: edge?.edgeId ?? uuidv4(),
            source: edge?.beginNodeId ?? predecessor.assemblyTaskId,
            target: edge?.endNodeId ?? task.assemblyTaskId,
            sourceHandle: edge?.beginHandlePosition ?? Position.Right,
            targetHandle: edge?.endHandlePosition ?? Position.Left,
          };
        }),
      ) ?? [];

    setNodes(!assembly ? autoLayoutedNodes(newNodes, newEdges) : newNodes);
    setEdges(newEdges);
  }, [part]);

  const saveAssembly = useCallback<AssemblyEditorContextType['saveAssembly']>(
    async (formValues) => {
      const assemblyPartItems: CreateEditPartBody['assemblyPartItems'] = nodes
        .filter((n) => n.type === 'part')
        .map(({ id, data }) => {
          const { quantity, part: nodePart } = data as NodeProps<PartNodeType>['data'];
          return {
            assemblyItemId: id as AssemblyItemId,
            ...nodePart,
            unitQuantity: quantity,
          };
        });
      const assemblyWriteInItems: CreateEditPartBody['assemblyWriteInItems'] = nodes
        .filter((n) => n.type === 'write-in')
        .map(({ id, data }) => {
          const { quantity, part: nodePart } = data as NodeProps<WriteInNodeType>['data'];
          return {
            assemblyItemId: id as AssemblyItemId,
            ...nodePart,
            unitQuantity: quantity,
          };
        });
      const assemblyTasks: CreateEditPartBody['assemblyTasks'] = nodes
        .filter((n) => n.type === 'task')
        .map(({ id, data }) => {
          const { taskType } = data as NodeProps<TaskNodeType>['data'];
          return {
            assemblyTaskId: id as AssemblyTaskId,
            taskTypeId: taskType.taskTypeId,
            predecessorAssemblyTaskIds: edges.filter((e) => e.target === id).map((e) => e.source as AssemblyTaskId),
          };
        });
      const assemblyEditorLayout: CustomAssembly = {
        version: 2,
        nodePositions: nodes.reduce(
          (out, node) => ({
            ...out,
            [node.id]: {
              x: node.position.x,
              y: node.position.y,
            },
          }),
          {},
        ),
        edges: edges.map((e) => ({
          edgeId: e.id as AssemblyEdgeId,
          beginNodeId: e.source as NodeReferenceId,
          endNodeId: e.target as NodeReferenceId,
          beginHandlePosition: e.sourceHandle as Position,
          endHandlePosition: e.targetHandle as Position,
        })),
      };
      return publishPart({
        formValues,
        assembly: {
          assemblyPartItems,
          assemblyWriteInItems,
          assemblyTasks,
          assemblyEditorLayout: stringifyCustomAssemblyJson(assemblyEditorLayout),
        },
      });
    },
    [edges, nodes, publishPart],
  );

  const navigate = useEvolveNavigate(part?.name ?? 'Assembly Editor', true);
  const catalogSetupUrl = useMemo<ShopNavigationRoute>(
    () => `/catalog-setup?categoryId=${part?.partCategoryId ?? form.values.partCategory?.partCategoryId ?? ''}`,
    [form.values.partCategory?.partCategoryId, part?.partCategoryId],
  );
  const goBackToCatalog = useCallback(() => {
    void navigate(catalogSetupUrl);
  }, [catalogSetupUrl, navigate]);

  const onSubmit = async (formValues: CreateEditPartFormType) => {
    setSelectedTab('details');
    await saveAssembly(formValues);
    goBackToCatalog();
  };

  return (
    <AssemblyEditorContext
      value={{
        nodes,
        edges,
        onNodesChange,
        onEdgesChange,
        onConnect,
        addNode,
        saveAssembly,
        isDirty: isDirty || form.isDirty(),
        form,
        catalogPart: part,
        publishing,
        catalogSetupUrl,
        goBackToCatalog,
      }}
    >
      <form style={{ width: '100%', height: '100%', display: 'flex', flex: 1 }} onSubmit={form.onSubmit(onSubmit)}>
        <ReactFlowProvider>{children}</ReactFlowProvider>
      </form>
    </AssemblyEditorContext>
  );
};

export const useAssemblyEditor = () => useGeneralContext(AssemblyEditorContext, 'AssemblyEditor');
